// The surviving dashboard (C0-R): the secondary "browse all models" surface,
// reachable from the sidebar — NOT the landing. It consumes ModelsProvider (shared
// with the sidebar), so creating/renaming/archiving here reflects in the sidebar
// and vice-versa. The freemium wall is owned by the shell (AppShell renders the
// UpgradePrompt from ModelsProvider), so create()/unarchive() here just trigger it.
import { useState, type FormEvent } from 'react';
import { useModels } from '../models/ModelsContext';
import { TopBarPortal } from '../app/topbar';
import ModelRow from '../components/ModelRow';
import UsageSummary from '../components/UsageSummary';
import FormBanner from '../components/FormBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

export default function BrowsePage() {
  const { active, archived, stats, loading, error, create, rename, archive, unarchive, remove } =
    useModels();
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    await create(newName); // navigates to the editor on success; raises the wall otherwise
    setNewName('');
    setCreating(false);
  }

  const wrap = (id: string, fn: (id: string) => Promise<void>) => async () => {
    setBusyId(id);
    try {
      await fn(id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <TopBarPortal>
        <span className="font-display text-sm font-medium">Browse all models</span>
      </TopBarPortal>

      <div className="mx-auto max-w-4xl px-6 py-8">
        <header className="mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Your models</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage everything you’ve created. Open a model to jump back into the editor.
          </p>
        </header>

        {error ? (
          <div className="mb-4">
            <FormBanner kind="error">{error}</FormBanner>
          </div>
        ) : null}

        {stats ? (
          <section className="mb-6">
            <UsageSummary stats={stats} />
          </section>
        ) : null}

        <form onSubmit={onCreate} className="mb-8 flex gap-2">
          <Input
            aria-label="New model name"
            placeholder="New model name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button type="submit" disabled={creating} className="shrink-0">
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </form>

        <section className="mb-8">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Active
          </h2>
          {loading ? (
            <ul className="space-y-2">
              {[0, 1].map((i) => (
                <li key={i}>
                  <Skeleton className="h-16 w-full rounded-lg" />
                </li>
              ))}
            </ul>
          ) : active.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
              No active models yet. Create one above — it opens straight in the editor.
            </p>
          ) : (
            <ul className="space-y-2">
              {active.map((m) => (
                <ModelRow
                  key={m.id}
                  model={m}
                  busy={busyId === m.id}
                  onRename={(id, name) => void rename(id, name)}
                  onArchive={wrap(m.id, archive)}
                  onUnarchive={wrap(m.id, unarchive)}
                  onDelete={wrap(m.id, (id) => remove(id))}
                />
              ))}
            </ul>
          )}
        </section>

        {archived.length > 0 ? (
          <section>
            <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Archived
            </h2>
            <ul className="space-y-2">
              {archived.map((m) => (
                <ModelRow
                  key={m.id}
                  model={m}
                  busy={busyId === m.id}
                  onRename={(id, name) => void rename(id, name)}
                  onArchive={wrap(m.id, archive)}
                  onUnarchive={wrap(m.id, unarchive)}
                  onDelete={wrap(m.id, (id) => remove(id))}
                />
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
